import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import SuperAdminService from "../../services/SuperAdminService";
import type {
  CreateUserPayload,
  SuperAdminRole,
  SuperAdminUser,
  UpdateUserPayload,
} from "../../services/SuperAdminService";
import LoadingSpinner from "../../components/LoadingSpinner";
import {
  useSuperAdminOutletContext,
} from "./SuperAdminLayout";

interface UserFormState {
  username: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  roleId?: string;
  isActive: boolean;
}

const defaultUserState: UserFormState = {
  username: "",
  email: "",
  password: "",
  firstName: "",
  lastName: "",
  roleId: "",
  isActive: true,
};

const UsersPage = () => {
  const { selectedOrganizationId, selectedOrganizationLabel } =
    useSuperAdminOutletContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formState, setFormState] = useState<UserFormState>(defaultUserState);
  const [editingUser, setEditingUser] = useState<SuperAdminUser | null>(null);
  const isEmailValid = (email: string) => /\S+@\S+\.\S+/.test(email);

  const {
    data: roles = [],
    isLoading: isLoadingRoles,
  } = useQuery({
    queryKey: ["super-admin", "roles", selectedOrganizationId],
    queryFn: () =>
      SuperAdminService.fetchRoles(selectedOrganizationId as string),
    enabled: Boolean(selectedOrganizationId),
  });

  const {
    data: users = [],
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
  } = useQuery({
    queryKey: ["super-admin", "users", selectedOrganizationId],
    queryFn: () =>
      SuperAdminService.fetchUsers(selectedOrganizationId as string),
    enabled: Boolean(selectedOrganizationId),
  });

  const createUserMutation = useMutation({
    mutationFn: ({ organizationId, payload }: { organizationId: string; payload: CreateUserPayload }) =>
      SuperAdminService.createUser(organizationId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "super-admin",
        "users",
        selectedOrganizationId,
      ]);
      setIsDialogOpen(false);
      setFormState(defaultUserState);
      enqueueSnackbar("Utilisateur créé avec succès", { variant: "success" });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Erreur lors de la création de l'utilisateur";
      enqueueSnackbar(message, { variant: "error" });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: UpdateUserPayload }) =>
      SuperAdminService.updateUser(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "super-admin",
        "users",
        selectedOrganizationId,
      ]);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => SuperAdminService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "super-admin",
        "users",
        selectedOrganizationId,
      ]);
      enqueueSnackbar("Utilisateur supprimé", { variant: "success" });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Erreur lors de la suppression";
      enqueueSnackbar(message, { variant: "error" });
    },
  });

  const handleOpenDialog = () => {
    setFormState(defaultUserState);
    setEditingUser(null);
    setIsDialogOpen(true);
  };

  const handleEditUser = (user: SuperAdminUser) => {
    setEditingUser(user);
    setFormState({
      username: user.username,
      email: user.email,
      password: "",
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      roleId: user.roleId || "",
      isActive: user.isActive,
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectRole = (event: SelectChangeEvent<string>) => {
    setFormState((prev) => ({ ...prev, roleId: event.target.value }));
  };

  const handleToggleActive = (
    _,
    checked: boolean,
    user: SuperAdminUser
  ) => {
    updateUserMutation.mutate({
      userId: user.id,
      payload: { isActive: checked },
    });
  };

  const handleChangeRole = (
    event: SelectChangeEvent<string>,
    user: SuperAdminUser
  ) => {
    const value = event.target.value;
    updateUserMutation.mutate({
      userId: user.id,
      payload: { roleId: value || undefined },
    });
  };

  const handleCreateUser = () => {
    if (!selectedOrganizationId) return;

    // Validation locale côté frontend
    if (!formState.username || !formState.email) {
      enqueueSnackbar("Nom d'utilisateur et email sont requis", {
        variant: "error",
      });
      return;
    }
    if (!isEmailValid(formState.email)) {
      enqueueSnackbar("Email invalide", { variant: "error" });
      return;
    }
    if (!editingUser) {
      if (!formState.password) {
        enqueueSnackbar("Mot de passe requis", { variant: "error" });
        return;
      }
      if (formState.password.length < 6) {
        enqueueSnackbar("Mot de passe trop court (min 6)", {
          variant: "error",
        });
        return;
      }
    } else if (formState.password && formState.password.length < 6) {
      enqueueSnackbar("Mot de passe trop court (min 6)", {
        variant: "error",
      });
      return;
    }

    if (editingUser) {
      const payload: UpdateUserPayload = {
        username: formState.username,
        email: formState.email,
        password: formState.password || undefined,
        firstName: formState.firstName || undefined,
        lastName: formState.lastName || undefined,
        roleId: formState.roleId || undefined,
        isActive: formState.isActive,
      };
      updateUserMutation.mutate({
        userId: editingUser.id,
        payload,
      });
      setIsDialogOpen(false);
      setFormState(defaultUserState);
      setEditingUser(null);
      return;
    }

    const payload: CreateUserPayload = {
      username: formState.username,
      email: formState.email,
      password: formState.password,
      firstName: formState.firstName || undefined,
      lastName: formState.lastName || undefined,
      roleId: formState.roleId || undefined,
      isActive: formState.isActive,
    };

    createUserMutation.mutate({
      organizationId: selectedOrganizationId,
      payload,
    });
  };

  const organizationName = useMemo(() => {
    return selectedOrganizationLabel || "Organisation";
  }, [selectedOrganizationLabel]);

  useEffect(() => {
    if (!isDialogOpen) {
      setFormState(defaultUserState);
    }
  }, [isDialogOpen]);

  if (!selectedOrganizationId) {
    return (
      <Alert severity="info">
        Aucune organisation sélectionnée. Utilisez le sélecteur en haut de page
        pour gérer les utilisateurs.
      </Alert>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Typography variant="h6" fontWeight={700}>
          Utilisateurs – {organizationName}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          fullWidth={isMobile}
        >
          Nouvel utilisateur
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: { xs: 1, sm: 2 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {isLoadingUsers || isFetchingUsers ? (
          <LoadingSpinner message="Chargement des utilisateurs..." />
        ) : isMobile ? (
          <Stack spacing={1.5}>
            {users.map((user) => (
              <Paper
                key={user.id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography fontWeight={700}>{user.username}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                  <Chip
                    label={user.isSuperAdmin ? "Super Admin" : "Membre"}
                    color={user.isSuperAdmin ? "primary" : "default"}
                    size="small"
                  />
                </Stack>

                <FormControl size="small" fullWidth>
                  <InputLabel id={`role-${user.id}`}>Rôle</InputLabel>
                  <Select
                    labelId={`role-${user.id}`}
                    value={user.roleId || ""}
                    label="Rôle"
                    onChange={(event) => handleChangeRole(event, user)}
                    disabled={user.isSuperAdmin}
                  >
                    <MenuItem value="">
                      <em>Aucun rôle</em>
                    </MenuItem>
                    {roles.map((role: SuperAdminRole) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2">Actif</Typography>
                  <Switch
                    color="primary"
                    checked={user.isActive}
                    disabled={user.isSuperAdmin}
                    onChange={(event, checked) =>
                      handleToggleActive(event, checked, user)
                    }
                  />
                  {!user.isSuperAdmin && (
                    <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
                      <EditIcon
                        fontSize="small"
                        color="action"
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleEditUser(user)}
                      />
                      <DeleteIcon
                        fontSize="small"
                        color="error"
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          if (
                            window.confirm(
                              "Supprimer cet utilisateur ? Cette action est irréversible."
                            )
                          ) {
                            deleteUserMutation.mutate(user.id);
                          }
                        }}
                      />
                    </Stack>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nom d'utilisateur</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell align="center">Actif</TableCell>
                <TableCell align="center">Super Admin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select
                        value={user.roleId || ""}
                        onChange={(event) => handleChangeRole(event, user)}
                        disabled={user.isSuperAdmin}
                      >
                        <MenuItem value="">
                          <em>Aucun rôle</em>
                        </MenuItem>
                        {roles.map((role: SuperAdminRole) => (
                          <MenuItem key={role.id} value={role.id}>
                            {role.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      color="primary"
                      checked={user.isActive}
                      disabled={user.isSuperAdmin}
                      onChange={(event, checked) =>
                        handleToggleActive(event, checked, user)
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    {user.isSuperAdmin ? (
                      <Chip label="Super Admin" color="primary" size="small" />
                    ) : (
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Chip label="Membre" size="small" />
                        <EditIcon
                          fontSize="small"
                          color="action"
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleEditUser(user)}
                        />
                        <DeleteIcon
                          fontSize="small"
                          color="error"
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            if (
                              window.confirm(
                                "Supprimer cet utilisateur ? Cette action est irréversible."
                              )
                            ) {
                              deleteUserMutation.mutate(user.id);
                            }
                          }}
                        />
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingUser ? "Modifier l'utilisateur" : "Nouvel utilisateur"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                size="small"
                label="Nom d'utilisateur"
                name="username"
                value={formState.username}
                onChange={handleFieldChange}
                fullWidth
                required
              />
              <TextField
                size="small"
                label="Email"
                name="email"
                value={formState.email}
                onChange={handleFieldChange}
                fullWidth
                required
              />
            </Stack>
            <TextField
              size="small"
              label="Mot de passe"
              name="password"
              type="password"
              value={formState.password}
              onChange={handleFieldChange}
              fullWidth
              required
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                size="small"
                label="Prénom"
                name="firstName"
                value={formState.firstName}
                onChange={handleFieldChange}
                fullWidth
              />
              <TextField
                size="small"
                label="Nom"
                name="lastName"
                value={formState.lastName}
                onChange={handleFieldChange}
                fullWidth
              />
            </Stack>
            <FormControl size="small" fullWidth>
              <InputLabel id="user-role-select">Rôle</InputLabel>
              <Select
                labelId="user-role-select"
                value={formState.roleId || ""}
                label="Rôle"
                onChange={handleSelectRole}
              >
                <MenuItem value="">
                  <em>Aucun rôle</em>
                </MenuItem>
                {roles.map((role: SuperAdminRole) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography>Activer l'utilisateur</Typography>
              <Switch
                checked={formState.isActive}
                onChange={(_, checked) =>
                  setFormState((prev) => ({ ...prev, isActive: checked }))
                }
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={createUserMutation.isLoading}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
